import {
  Autocomplete, Box, Button, Checkbox, Chip, FormControl, FormControlLabel, IconButton, InputLabel,
  ListSubheader, MenuItem, Select, TextField, Tooltip, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  addChild, newCondition, newGroup, OPERATOR_LABELS, operatorsFor, questionMenu, removeNode, setOperator,
  setQuestion, updateNode, valueCount,
} from './logicModel';

const CONNECTOR_LABELS = { and: 'AND', or: 'OR', unknown: 'AND / OR?' };
const nextOp = (op) => (op === 'and' ? 'or' : 'and');

/** The connector between a group's rows: one per group, so clicking any switches them all. */
const Connector = ({ op, onClick }) => (
  <Tooltip title="Click to switch this whole group between AND and OR">
    <Button
      size="small"
      variant={op === 'unknown' ? 'contained' : 'outlined'}
      color={op === 'unknown' ? 'warning' : 'primary'}
      onClick={onClick}
      aria-label={`Connector ${CONNECTOR_LABELS[op]}: click to switch`}
      sx={{ minWidth: 0, px: 1.25, py: 0, my: 0.5, fontWeight: 700, fontSize: 12, borderRadius: 4 }}
    >
      {CONNECTOR_LABELS[op]}
    </Button>
  </Tooltip>
);

const QuestionSelect = ({ node, menu, index, onChange }) => {
  const current = index.get(node.questionId);
  const listed = menu.some((item) => item.type === 'question' && item.question.question_id === node.questionId);
  return (
    <FormControl size="small" sx={{ minWidth: 230, flex: '1 1 230px' }}>
      <InputLabel id={`question-${node.id}`}>Question</InputLabel>
      <Select
        labelId={`question-${node.id}`}
        label="Question"
        value={node.questionId ?? ''}
        onChange={(e) => onChange(index.get(e.target.value))}
        renderValue={() => (current ? current.label : node.questionId != null ? `Question ${node.questionId}` : '')}
        MenuProps={{ PaperProps: { sx: { maxHeight: 420 } } }}
      >
        {/* A question the list no longer offers (unusable, or gone) still shows as chosen. */}
        {!listed && node.questionId != null && (
          <MenuItem value={node.questionId} disabled>{current ? current.label : `Question ${node.questionId}`}</MenuItem>
        )}
        {menu.map((item) => (item.type === 'heading' ? (
          <ListSubheader key={item.key} sx={{ lineHeight: '32px' }}>{index.get(item.question.question_id)?.label}</ListSubheader>
        ) : (
          <MenuItem key={item.key} value={item.question.question_id} sx={{ pl: item.indent ? 4 : 2 }}>
            {item.indent ? item.question.title : index.get(item.question.question_id)?.label}
          </MenuItem>
        )))}
      </Select>
    </FormControl>
  );
};

const NumberField = ({ label, value, onChange }) => (
  <TextField
    size="small" type="number" label={label} value={value ?? ''} sx={{ width: 110 }}
    onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
  />
);

/** The answers for a row: tick boxes for choices, numbers, or exact texts. */
const Values = ({ node, question, onChange }) => {
  const count = valueCount(node.operator);
  if (!question || count === 0) return null;
  if (count === 'many' && question.options?.length) {
    const toggle = (optionId) => onChange(node.values.includes(optionId)
      ? node.values.filter((v) => v !== optionId) : [...node.values, optionId]);
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1, flexBasis: '100%', pl: 0.5 }}>
        {question.options.map((o) => (
          <FormControlLabel key={o.option_id} label={o.title || o.reporting_value} sx={{ mr: 1 }}
            control={<Checkbox size="small" checked={node.values.includes(o.option_id)} onChange={() => toggle(o.option_id)} />} />
        ))}
      </Box>
    );
  }
  if (count === 'many') {
    return (
      <Autocomplete
        multiple freeSolo size="small" options={[]} value={node.values.map(String)}
        onChange={(e, values) => onChange(values.map((v) => v.trim()).filter(Boolean))}
        renderTags={(values, getTagProps) => values.map((v, i) => <Chip size="small" label={v} {...getTagProps({ index: i })} key={v} />)}
        renderInput={(params) => <TextField {...params} label="Exact text (Enter after each)" helperText="Matching is exact and case-sensitive." />}
        sx={{ flex: '1 1 260px' }}
      />
    );
  }
  const set = (i) => (v) => {
    const values = [...node.values];
    values[i] = v;
    onChange(values);
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <NumberField label="Number" value={node.values[0]} onChange={set(0)} />
      {count === 2 && <><Typography variant="body2">and</Typography><NumberField label="Number" value={node.values[1]} onChange={set(1)} /></>}
    </Box>
  );
};

const ConditionRow = ({ node, tree, onChange, menu, index, problem }) => {
  const question = index.get(node.questionId);
  const operators = operatorsFor(question);
  return (
    <Box sx={{ py: 0.75 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <QuestionSelect node={node} menu={menu} index={index} onChange={(q) => q && onChange(setQuestion(tree, node.id, q))} />
        <FormControl size="small" sx={{ minWidth: 200 }} disabled={!operators.length}>
          <InputLabel id={`compare-${node.id}`}>Compare</InputLabel>
          <Select labelId={`compare-${node.id}`} label="Compare" value={operators.includes(node.operator) ? node.operator : ''}
            onChange={(e) => onChange(setOperator(tree, node.id, e.target.value, node.operator))}>
            {operators.map((op) => <MenuItem key={op} value={op}>{OPERATOR_LABELS[op]}</MenuItem>)}
          </Select>
        </FormControl>
        <Tooltip title="Remove this condition">
          <IconButton size="small" aria-label="Remove condition" onClick={() => onChange(removeNode(tree, node.id))} sx={{ mt: 0.5 }}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Values node={node} question={question} onChange={(values) => onChange(updateNode(tree, node.id, { values }))} />
      </Box>
      {problem && <Typography variant="caption" color="error" sx={{ pl: 0.5 }}>{problem}</Typography>}
    </Box>
  );
};

const Group = ({ group, tree, onChange, menu, index, problems, depth }) => (
  <Box sx={depth === 0 ? {} : {
    borderLeft: '3px solid', borderColor: 'primary.light', pl: 1.5, my: 0.5, py: 0.5,
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03), borderRadius: 1,
  }}>
    {group.children.map((child, i) => (
      <Box key={child.id}>
        {i > 0 && <Connector op={group.op} onClick={() => onChange(updateNode(tree, group.id, { op: nextOp(group.op) }))} />}
        {child.type === 'group'
          ? <Group group={child} tree={tree} onChange={onChange} menu={menu} index={index} problems={problems} depth={depth + 1} />
          : <ConditionRow node={child} tree={tree} onChange={onChange} menu={menu} index={index} problem={problems[child.id]} />}
      </Box>
    ))}
    {problems[group.id] && <Typography variant="caption" color="error" component="p">{problems[group.id]}</Typography>}
    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
      <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => onChange(addChild(tree, group.id, newCondition()))}
        sx={{ textTransform: 'none' }}>
        Add condition
      </Button>
      <Button size="small" startIcon={<AddRoundedIcon />}
        onClick={() => onChange(addChild(tree, group.id, newGroup(nextOp(group.op === 'unknown' ? 'or' : group.op))))}
        sx={{ textTransform: 'none' }}>
        Add group
      </Button>
      {depth > 0 && (
        <Button size="small" color="error" onClick={() => onChange(removeNode(tree, group.id))} sx={{ textTransform: 'none' }}>
          Remove group
        </Button>
      )}
    </Box>
  </Box>
);

/**
 * Alchemer-style condition rows (design §3): question, comparison, answers; Add condition / Add
 * group; one AND / OR per group, visible and clickable. `problems` is validate()'s list.
 */
const ConditionBuilder = ({ tree, onChange, questions, index, problems = [] }) => {
  const menu = questionMenu(questions);
  const byId = Object.fromEntries(problems.map((p) => [p.id, p.message]));
  return (
    <Box>
      <Group group={tree} tree={tree} onChange={onChange} menu={menu} index={index} problems={byId} depth={0} />
      {tree.children.length > 0 && (
        <Button size="small" color="error" onClick={() => onChange({ ...tree, children: [] })} sx={{ textTransform: 'none', mt: 0.5 }}>
          Remove all logic
        </Button>
      )}
    </Box>
  );
};

export default ConditionBuilder;
